import "@config/i18n";
import { Suspense, useEffect, useState } from "react";
import type React from "react";
import type { MantineThemeOverride } from "@mantine/core";
// Brand theme, tokens and components all come from the shared design system.
// Import its styles here (in the exposed component, not just main.tsx) so they
// load when the shell mounts this module via federation.
import { XPlatformProvider } from "@xplatform/design-system";
import "@mantine/core/styles.css";
import "@xplatform/design-system/styles.css";
import { Routes, Route, Navigate } from "react-router-dom";
import { injectStylesheet } from "@utils/injectStyles";
import { ModuleContextProvider } from "@src/ContextProvider";
import { ReviewApiProvider } from "@services/ReviewApiProvider";
import { PermissionGuard } from "@components/PermissionGuard";
import { LoadingScreen } from "@components/LoadingScreen";
import { ReviewWorkspace } from "@features/review";

const PORTAL_ID = "xplatform-generation-frontend-portal-root";

export interface ModuleAppProps {
  /**
   * Injected ReviewApi, used by the standalone preview server to supply the
   * in-memory fake API. In the shell / a real build this is undefined and the
   * provider builds the real client from VITE_API_BASE_URL.
   */
  api?: React.ComponentProps<typeof ReviewApiProvider>["api"];
}

const ModuleApp = ({ api }: ModuleAppProps = {}) => {
  // XPlatformProvider already applies the TecAlliance brand theme synchronously
  // (no fallback flash). At runtime we still let the shell's federated theme
  // win if present, by passing it as an override.
  const [shellOverride, setShellOverride] = useState<
    MantineThemeOverride | undefined
  >(undefined);

  useEffect(() => {
    import("xplatform_shell/theme")
      .then((mod) => {
        if (mod.theme) setShellOverride(mod.theme);
      })
      .catch(() => {});
    const base = import.meta.env.BASE_URL || "/";
    injectStylesheet(`${base}assets/style.css`);
  }, []);

  return (
    <XPlatformProvider theme={shellOverride}>
      <ModuleContextProvider>
        <ReviewApiProvider api={api}>
          {/* Any generation tier (access, review or admin) sees the module (D21). */}
          <PermissionGuard require="access">
            <Suspense fallback={<LoadingScreen message="Loading..." />}>
              <Routes>
                <Route index element={<Navigate to="review" replace />} />
                {/* review or admin (D21) gates the whole review workspace. The
                    three deep-linkable paths all render the one workspace
                    (which folds the old queue / run / image pages): `review`
                    auto-selects the first run awaiting review and its first
                    pending image; the two deeper paths pin a run / image. */}
                <Route
                  path="review"
                  element={
                    <PermissionGuard require="review">
                      <ReviewWorkspace />
                    </PermissionGuard>
                  }
                />
                <Route
                  path="review/:runId"
                  element={
                    <PermissionGuard require="review">
                      <ReviewWorkspace />
                    </PermissionGuard>
                  }
                />
                <Route
                  path="review/:runId/:requestId/:objId"
                  element={
                    <PermissionGuard require="review">
                      <ReviewWorkspace />
                    </PermissionGuard>
                  }
                />
                {/* F6 developer runs view lands here last (D13). */}
              </Routes>
            </Suspense>
          </PermissionGuard>
        </ReviewApiProvider>
      </ModuleContextProvider>
      <div
        id={PORTAL_ID}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          zIndex: 9999,
        }}
      />
    </XPlatformProvider>
  );
};

export default ModuleApp;
